/**
 * PathwayView (v2) view - Sprint 4.1 REDO.
 *
 * Replaces the Phase 1 placeholder + the stopped Three.js dispatch with
 * the prototype-anchored Metabolic Dashboard layout. Source-of-truth:
 * the live prototype at
 * https://ai-native-prototype.vercel.app/care-pathway-studio.html
 * PathwayView tab. The prototype is a card-based dashboard, NOT a
 * Three.js silhouette - the Plan Mode v8 §5.1 description was wrong and
 * is superseded by Sprint 4.1 REDO.
 *
 * Layout (top to bottom):
 *   1. Patient journey headline (verbatim from the prototype)
 *   2. IllustrativeProjectionBanner (ADR-001 Layer 1; PROD-570 IPB-4)
 *   3. MetabolicDashboard (3-column status grid)
 *   4. PresenterActionBar (sticky bottom)
 *   5. PromCollectionSubtab (TLP1 schedule)
 *
 * Per HC5: zero em / en dashes anywhere.
 * Per HC2: every color, spacing, radius value resolves through Signal
 * DS tokens (no inline hex).
 * Per ADR-001 Layer 1: the IllustrativeProjectionBanner is mounted
 * persistently above the metric content; PROD-570 IPB-4 integration
 * test asserts this for the PathwayView surface.
 */
import { IllustrativeProjectionBanner } from '../components/IllustrativeProjectionBanner';
import { MetabolicDashboard } from '../components/pathway-view/MetabolicDashboard';
import { PresenterActionBar } from '../components/pathway-view/PresenterActionBar';
import { PromCollectionSubtab } from '../components/pathway-view/PromCollectionSubtab';
import {
  PATHWAY_VIEW_HEADLINE,
  buildPatientJourneySubtitle,
} from '../components/pathway-view/fixtures';
import { usePatientStore } from '../state/patient.store';
import { getPatientById, getDefaultPatient } from '../data/synthetic-patients';

export function PathwayView() {
  const activePatientId = usePatientStore((s) => s.activePatientId);
  const patient =
    (activePatientId ? getPatientById(activePatientId) : null) ??
    getDefaultPatient();
  return (
    <section
      aria-labelledby="view-title-pv"
      className="relative px-gutter-lg py-gutter-xl"
    >
      <header className="flex flex-col gap-gutter-sm">
        <h2
          id="view-title-pv"
          className="text-signal-2xl font-semibold text-text-primary"
        >
          {PATHWAY_VIEW_HEADLINE}
        </h2>
        <p
          data-pathway-view-subtitle="patient-journey"
          className="text-signal-sm text-text-secondary"
        >
          {buildPatientJourneySubtitle(patient)}
        </p>
      </header>
      <IllustrativeProjectionBanner
        variant="block-prefix"
        className="mt-gutter-md max-w-3xl"
      />
      <div className="mt-gutter-lg">
        <MetabolicDashboard />
      </div>
      <div className="mt-gutter-lg">
        <PresenterActionBar />
      </div>
      <div className="mt-gutter-xl">
        <PromCollectionSubtab />
      </div>
    </section>
  );
}
